import { useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { useSEO } from "@/lib/seo";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, CheckCircle2 } from "lucide-react";

const inputCls = "rounded-sm border border-ink/15 bg-white focus:ring-1 focus:ring-gold focus:border-gold h-10 text-sm w-full px-3 outline-none";

export default function AdminSetup() {
  useSEO("Admin Password Setup | Maqline Solutions", "Secure admin password setup.");
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [pw1, setPw1] = useState("");
  const [pw2, setPw2] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (pw1 !== pw2) { toast.error("Passwords do not match"); return; }
    setLoading(true);
    try {
      await api.post("/admin/confirm-setup", { token, new_password: pw1 });
      setDone(true);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-darknavy noise-bg flex items-center justify-center px-5 pt-20" data-testid="admin-setup">
      <div className="w-full max-w-sm bg-white rounded-sm p-8 border border-gold/25">
        <img src="/maqline-logo.png" alt="Maqline" className="h-8 w-auto mb-6" />
        {done ? (
          <div className="text-center" data-testid="admin-setup-success">
            <CheckCircle2 size={36} className="text-gold mx-auto mb-4" />
            <h1 className="font-display text-2xl text-ink mb-2">Password updated</h1>
            <p className="text-sm text-muted mb-6">Your new admin password is now active. All previous setup links are invalidated.</p>
            <Link to="/admin" data-testid="admin-setup-goto-login" className="block w-full bg-navy text-white font-semibold text-sm py-3.5 rounded-sm hover:bg-darknavy transition-colors text-center">
              Go to Admin Sign In
            </Link>
          </div>
        ) : (
          <form onSubmit={submit}>
            <h1 className="font-display text-2xl text-ink mb-1">Set New Admin Password</h1>
            <p className="text-xs text-muted mb-6">Minimum 12 characters, with uppercase, lowercase, and a number.</p>
            {!token && <p className="text-xs text-red-600 mb-4" data-testid="admin-setup-no-token">This setup link is missing its token. Please use the full link from your email.</p>}
            <label className="block text-xs uppercase tracking-wider font-semibold text-ink/70 mb-1.5">New Password</label>
            <input data-testid="admin-setup-password" type="password" className={inputCls + " mb-4"} value={pw1} onChange={(e) => setPw1(e.target.value)} required minLength={12} />
            <label className="block text-xs uppercase tracking-wider font-semibold text-ink/70 mb-1.5">Confirm New Password</label>
            <input data-testid="admin-setup-password-confirm" type="password" className={inputCls + " mb-6"} value={pw2} onChange={(e) => setPw2(e.target.value)} required minLength={12} />
            <button data-testid="admin-setup-submit" disabled={loading || !token} className="btn-sweep w-full bg-navy text-white font-semibold text-sm py-3.5 rounded-sm hover:bg-darknavy transition-colors disabled:opacity-60 flex items-center justify-center gap-2">
              {loading && <Loader2 size={15} className="animate-spin" />} Set Password
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
